import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, Calendar, Star, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rating Breakdown Component
const RatingBreakdown = ({ breakdown, totalRatings }) => {
  const stars = ['5', '4', '3', '2', '1'];
  
  return (
    <div className="space-y-2">
      {stars.map((star) => {
        const count = breakdown?.[star] || 0;
        const percentage = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
        
        return (
          <div key={star} className="flex items-center gap-2 text-sm">
            <span className="w-3 text-muted-foreground">{star}</span>
            <Star className="w-3 h-3 text-yellow-500 fill-current" />
            <div className="flex-1 h-2 bg-muted overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="h-full bg-yellow-500"
              />
            </div>
            <span className="w-8 text-xs text-muted-foreground text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

// Star Rating Input Component
const StarRating = ({ value, onChange, disabled }) => {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className={`p-1 transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        >
          <Star
            className={`w-6 h-6 transition-colors ${
              star <= (hovered || value)
                ? 'text-yellow-500 fill-current'
                : 'text-muted-foreground'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export const GalleryDetail = () => {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  
  // Rating state
  const [userRating, setUserRating] = useState(0);
  const [hasRated, setHasRated] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${API}/galleries/${id}`);
        if (response.ok) {
          const data = await response.json();
          setGallery(data);
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
    
    // Check if already rated (via cookie check or local state)
    const ratedGalleries = document.cookie
      .split('; ')
      .find(row => row.startsWith('rated_galleries='))
      ?.split('=')[1] || '';
    if (ratedGalleries.split(',').includes(id)) {
      setHasRated(true);
    }
  }, [id]);

  const submitRating = async () => {
    if (userRating === 0 || hasRated) return;
    
    setSubmittingRating(true);
    try {
      const response = await fetch(`${API}/gallery/${id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: userRating })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGallery(prev => ({
          ...prev,
          rating_sum: Math.round(data.average_rating * data.total_ratings),
          rating_count: data.total_ratings,
          rating_breakdown: data.rating_breakdown
        }));
        setHasRated(true);
        toast.success('¡Gracias por tu valoración!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al enviar valoración');
      }
    } catch (error) {
      toast.error('Error al enviar valoración');
    } finally {
      setSubmittingRating(false);
    }
  };

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (diff > threshold) nextImage();
    else if (diff < -threshold) prevImage();
  };

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const nextImage = useCallback(() => {
    if (gallery) {
      setCurrentImage((prev) => (prev + 1) % gallery.images.length);
    }
  }, [gallery]);

  const prevImage = useCallback(() => {
    if (gallery) {
      setCurrentImage((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
    }
  }, [gallery]);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch (e.key) {
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage, closeLightbox]);

  // Calculate average rating
  const averageRating = gallery?.rating_count > 0 
    ? (gallery.rating_sum / gallery.rating_count).toFixed(1) 
    : null;

  if (loading) {
    return (
      <main className="pt-24 pb-20 min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="h-96 bg-muted animate-pulse" />
        </div>
      </main>
    );
  }

  if (!gallery) {
    return (
      <main className="pt-24 pb-20 min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="font-serif text-4xl mb-4">Galería no encontrada</h1>
          <Link to="/#portfolio" className="text-muted-foreground hover:text-foreground">
            Volver al portfolio
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-20 min-h-screen bg-background" data-testid="gallery-detail-page">
      <SEO 
        title={gallery.title}
        description={gallery.description || `Galería de fotografías: ${gallery.title}. ${gallery.category} en ${gallery.location}.`}
        image={gallery.thumbnail || gallery.images?.[0]}
        url={`https://jonquintano.com/gallery/${gallery.gallery_id}`}
      />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Back Button */}
        <Link 
          to="/#portfolio" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-portfolio"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al portfolio
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {gallery.location}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {gallery.date}
            </span>
            {averageRating && (
              <span className="flex items-center gap-1 text-amber-500">
                <Star className="w-4 h-4 fill-current" />
                {averageRating} ({gallery.rating_count} {gallery.rating_count === 1 ? 'voto' : 'votos'})
              </span>
            )}
            <span className="px-2 py-1 bg-muted text-xs uppercase tracking-widest">
              {gallery.category}
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-4">
            {gallery.title}
          </h1>
          <p className="text-muted-foreground max-w-3xl">{gallery.description}</p>
        </motion.div>

        {/* Rating Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 p-6 bg-card border border-border"
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Rate this gallery */}
            <div>
              <h3 className="font-medium mb-3">
                {hasRated ? '¡Gracias por tu valoración!' : '¿Te gusta esta galería?'}
              </h3>
              <div className="flex items-center gap-4">
                <StarRating 
                  value={userRating} 
                  onChange={setUserRating}
                  disabled={hasRated}
                />
                {!hasRated && userRating > 0 && (
                  <Button 
                    onClick={submitRating}
                    disabled={submittingRating}
                    size="sm"
                  >
                    {submittingRating ? 'Enviando...' : 'Enviar'}
                  </Button>
                )}
              </div>
              {hasRated && (
                <p className="text-sm text-muted-foreground mt-2">
                  Tu valoración ayuda a otros visitantes
                </p>
              )}
            </div>
            
            {/* Rating breakdown */}
            {gallery.rating_count > 0 && (
              <div>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-4xl font-serif">{averageRating}</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= Math.round(parseFloat(averageRating))
                            ? 'text-yellow-500 fill-current'
                            : 'text-muted-foreground'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {gallery.rating_count} {gallery.rating_count === 1 ? 'valoración' : 'valoraciones'}
                  </span>
                </div>
                <RatingBreakdown 
                  breakdown={gallery.rating_breakdown} 
                  totalRatings={gallery.rating_count} 
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Image Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="columns-2 md:columns-3 lg:columns-4 gap-3"
        >
          {gallery.images.map((image, index) => (
            <div
              key={index}
              className="cursor-pointer overflow-hidden group mb-3 break-inside-avoid"
              onClick={() => openLightbox(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image}
                alt={`${gallery.title} - ${index + 1}`}
                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
            onClick={(e) => e.target === e.currentTarget && closeLightbox()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-testid="lightbox"
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Navigation */}
            <button
              onClick={prevImage}
              className="absolute left-4 z-10 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 z-10 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Image */}
            <motion.img
              key={currentImage}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              src={gallery.images[currentImage]}
              alt={`${gallery.title} - ${currentImage + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
              {currentImage + 1} / {gallery.images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default GalleryDetail;
